import { Dialog, Paper } from "@mui/material";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  useGaitResultsActions,
  useUpdatingGaitResultsActions,
} from "../../../../../hooks/GaitAnalysis/actions";
import {
  useGaitResults,
  useRelativeLandmarks,
  useSensorDataRefactored,
} from "../../../../../hooks/GaitAnalysis/results";
import CycleChart from "./CycleChart";
import PressureMetrics from "./PressureMetrics";
import Headline from "./Headline";
import Replay from "./Replay";
import ResultsMetrics from "./ResultsMetrics";
import ViewToggle from "./viewToggle";
import html2canvas from "html2canvas";
import PressureMap from "./PressureMap";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import ResultPdfDownloadButton from "./pdf/ResultPdfDownloadButton";
import ResultPdfSendButton from "./pdf/ResultPdfSendButton";
import FlexionAnalysisContainer from "./FlexionAnalysisContainer";
import { ToastContainer } from "react-toastify";

export default function ObservationResult() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [viewType, setViewType] = React.useState("patient");
  const gaitResults = useSelector(({ gaitResults }) => gaitResults);
  const lastGaitResults = useSelector(({ lastGaitResults }) => lastGaitResults);
  const { updateResultsLocally } = useUpdatingGaitResultsActions();
  const relativeLandmarks = useRelativeLandmarks(0);

  const cycleRef = React.useRef(null);
  const [cycleImage, setCycleImage] = React.useState(null);
  const pressureRef = React.useRef(null);
  const [pressureImage, setPressureImage] = React.useState(null);
  const pressureMapRef = React.useRef(null);
  const [pressureMapImage, setPressureMapImage] = React.useState(null);
  const doctorsMetricsRef = React.useRef(null);
  const [doctorMetricsImage, setDoctorMetricsImage] = React.useState(null);
  const [sensorData, setSensorData] = useState(useSensorDataRefactored());
  const [bannerNotEnoughSteps, setBannerNotEnoughSteps] = useState(false);
  const { addResults } = useGaitResultsActions();
  const dispatch = useDispatch();
  const store = useStore();
  const [metrics, setMetrics] = React.useState(null);
  const patientInfo = useSelector(
    ({ gaitPatientInfo }) => gaitPatientInfo.values
  );

  // Image loading states
  const [imagesLoading, setImagesLoading] = React.useState({
    cycleImage: false,
    pressureImage: false,
    pressureMapImage: false,
    doctorMetricsImage: false,
  });
  const [allImagesLoaded, setAllImagesLoaded] = React.useState(false);
  const [pdfReady, setPdfReady] = React.useState(false);
  console.log("gait id!", gaitResults.id);
  console.log("🎯 GAIT RESULTS DEBUG - Full gaitResults object:", gaitResults);
  console.log("🎯 GAIT RESULTS DEBUG - time_series_data:", gaitResults.time_series_data);
  console.log("🎯 GAIT RESULTS DEBUG - Available keys:", Object.keys(gaitResults));

  React.useEffect(() => {
    const gaitResults = store.getState().gaitResults;
    if (gaitResults.metrics) {
      setMetrics(gaitResults.metrics);
      console.log("gait id", gaitResults.id, gaitResults.userId);

      // failed to add to firestore
      if (gaitResults.userId && !gaitResults.id && !gaitResults.userId) {
        addResults().then((results) => {
          dispatch({
            type: "SET_GAIT_RESULTS",
            payload: { id: results?.id },
          });
        });
      }
    } else {
      const lastGaitResults = store.getState().lastGaitResults;
      // if (!lastGaitResults.docs) return;
      if (lastGaitResults.docs.length > 0) {
        setMetrics(lastGaitResults.docs[0].metrics);
      } else {
        const gaitResults = store.getState().gaitResults;
        setMetrics(gaitResults.metrics);
      }
    }
  });

  useEffect(() => {
    setLoading(true);
    if (sensorData && Object.keys(sensorData).length === 0) {
      setSensorData(gaitResults.sensorData);
    }
  }, [gaitResults.sensorData]);

  let availability = {};
  let gaitResult = useGaitResults();
  if (lastGaitResults.docs.length === 0) {
    availability = gaitResult;
  }

  useEffect(() => {
    if (!bannerNotEnoughSteps) {
      setBannerNotEnoughSteps(availability.notEnoughData);
    }
  }, [availability.notEnoughData]);

  useEffect(() => {
    if (lastGaitResults.docs.length > 0) {
      console.log(lastGaitResults.docs[0], "last gait results");
      updateResultsLocally(lastGaitResults.docs[0]);
    }
  }, [lastGaitResults.docs]);

  // Check if all images are loaded
  useEffect(() => {
    const allLoaded = Object.values(imagesLoading).every(loaded => loaded === true);
    setAllImagesLoaded(allLoaded);
    
    if (allLoaded) {
      // Additional delay to ensure all images are fully rendered
      setTimeout(() => {
        setPdfReady(true);
      }, 1000);
    }
  }, [imagesLoading]);

  // Fallback timeout to enable PDF download even if some images fail
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!pdfReady) {
        console.warn("Image loading timeout - enabling PDF download with available images");
        setPdfReady(true);
      }
    }, 15000); // 15 second timeout

    return () => clearTimeout(timeout);
  }, [pdfReady]);

  useEffect(() => {
    const captureDivToImage = async () => {
      // Reset loading states
      setImagesLoading({
        cycleImage: false,
        pressureImage: false,
        pressureMapImage: false,
        doctorMetricsImage: false,
      });
      setPdfReady(false);

      try {
        // delay by 3 seconds to allow the charts to render completely
        await new Promise((resolve) => setTimeout(resolve, 3000));
        
        // Capture cycle image
        if (cycleRef.current) {
          const canvas = await html2canvas(cycleRef.current, {
            useCORS: true,
            allowTaint: true,
            scale: 2, // Higher resolution
          });
          const imgData = canvas.toDataURL("image/png");
          setCycleImage(imgData);
          setImagesLoading(prev => ({ ...prev, cycleImage: true }));
        }
      } catch (error) {
        setCycleImage(null);
        setImagesLoading(prev => ({ ...prev, cycleImage: true }));
        console.error("Cycle image capture error:", error);
      }

      try {
        // Capture pressure image
        if (pressureRef.current) {
          const canvas2 = await html2canvas(pressureRef.current, {
            useCORS: true,
            allowTaint: true,
            scale: 2,
          });
          const imgData2 = canvas2.toDataURL("image/png");
          setPressureImage(imgData2);
          setImagesLoading(prev => ({ ...prev, pressureImage: true }));
        }
      } catch (error) {
        setPressureImage(null);
        setImagesLoading(prev => ({ ...prev, pressureImage: true }));
        console.error("Pressure image capture error:", error);
      }

      try {
        // Capture pressure map image
        if (pressureMapRef.current) {
          const canvas3 = await html2canvas(pressureMapRef.current, {
            useCORS: true,
            allowTaint: true,
            scale: 2,
          });
          const imgData3 = canvas3.toDataURL("image/png");
          setPressureMapImage(imgData3);
          setImagesLoading(prev => ({ ...prev, pressureMapImage: true }));
        }
      } catch (error) {
        setPressureMapImage(null);
        setImagesLoading(prev => ({ ...prev, pressureMapImage: true }));
        console.error("Pressure map image capture error:", error);
      }

      try {
        // Capture doctor metrics image
        if (doctorsMetricsRef.current) {
          const canvas4 = await html2canvas(doctorsMetricsRef.current, {
            scrollY: -window.scrollY,
            scrollX: -window.scrollX,
            useCORS: true,
            allowTaint: true,
            scale: 2,
          });
          const imgData4 = canvas4.toDataURL("image/png");
          setDoctorMetricsImage(imgData4);
          setImagesLoading(prev => ({ ...prev, doctorMetricsImage: true }));
        }
      } catch (error) {
        setDoctorMetricsImage(null);
        setImagesLoading(prev => ({ ...prev, doctorMetricsImage: true }));
        console.error("Doctor metrics image capture error:", error);
      }
    };
    
    if (loading && metrics) {
      captureDivToImage().catch((err) => {
        console.error("Image capture error:", err);
      });
    }
  }, [loading, metrics]);

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Hidden div for PDF capture - FIXED: made visible for html2canvas */}
      <div
        ref={doctorsMetricsRef}
        style={{
          height: 900,
          position: "absolute",
          opacity: 1, // Changed from 0 to 1
          zIndex: -1,
          // Removed visibility: "hidden"
        }}
      >
        <Grid container sx={{ margin: 2 }}>
          <Grid item xs={12} sx={{ ml: 2 }}>
            <Headline
              fontSize={30}
              text={"Metrics"}
              height={"5vh"}
              // backgroundColor={'rgb(60,232,170)'}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item xs={12}>
            <ResultsMetrics />
          </Grid>
        </Grid>
        {/* Hidden FlexionAnalysisContainer for PDF - charts are hidden */}
        {false && (
          <Grid container spacing={2} sx={{ padding: 2 }}>
            <Grid item xs={12}>
              <FlexionAnalysisContainer 
                timeSeriesData={gaitResults.time_series_data}
                flexionSummary={gaitResults.flexion_summary}
                statistics={gaitResults.statistics}
              />
            </Grid>
          </Grid>
        )}
      </div>

      <Dialog open={bannerNotEnoughSteps} fullWidth={true} maxWidth={"lg"}>
        <div style={{ padding: 50 }}>
          <Typography variant={"h4"}>
            Not enough valid steps collected, please collect more next time!
          </Typography>
          <Button
            onClick={() => {
              navigate("/gait_analysis/backend");
            }}
          >
            close
          </Button>
        </div>
      </Dialog>

      <Paper sx={{ paddingBottom: 3, marginBottom: 2 }}>
        <Grid
          container
          sx={{ justifyContent: "center", display: "flex", mt: 2 }}
        >
          <ViewToggle viewType={viewType} setViewType={setViewType} />
        </Grid>
        
        <div ref={cycleRef}>
          <CycleChart setLoading={setLoading} metrics={metrics} />
        </div>
        
        <div ref={pressureMapRef}>
          <PressureMap
            setLoading={setLoading}
            sensorData={sensorData}
            gaitResults={gaitResults}
          />
        </div>
        
        <div ref={pressureRef}>
          <PressureMetrics setLoading={setLoading} sensorData={sensorData} />
        </div>
        
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {/* Loading indicator */}
          {!pdfReady && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              marginBottom: "10px"
            }}>
              <div style={{
                width: "20px",
                height: "20px",
                border: "2px solid #f3f3f3",
                borderTop: "2px solid #3498db",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }} />
              <span>Preparing PDF... Loading images ({Object.values(imagesLoading).filter(Boolean).length}/4)</span>
            </div>
          )}
          
          <div style={{ display: "flex", gap: "10px" }}>
            <ResultPdfDownloadButton
              doctorMetricsImage={doctorMetricsImage}
              cycleImage={cycleImage}
              pressureImage={pressureImage}
              pressureMapImage={pressureMapImage}
              metrics={metrics}
              patientInfo={patientInfo}
              disabled={!pdfReady}
              loading={!pdfReady}
            />
            <ResultPdfSendButton
              doctorMetricsImage={doctorMetricsImage}
              cycleImage={cycleImage}
              pressureImage={pressureImage}
              pressureMapImage={pressureMapImage}
              metrics={metrics}
              patientInfo={patientInfo}
              disabled={!pdfReady}
              loading={!pdfReady}
            />
          </div>
        </div>
        
        {/* Hidden Replay component with relative landmarks */}
        {false && (
          <div
            style={{
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Grid container spacing={2} sx={{ padding: 2 }}>
              <Grid item xs={3} />
              <Grid item xs={6}>
                <Replay relativeLandmarks={relativeLandmarks} />
              </Grid>
              <Grid item xs={3} />
            </Grid>
          </div>
        )}
        
        {viewType === "doctor" && (
          <div>
            <Grid container sx={{ margin: 2 }}>
              <Grid item xs={12} sx={{ ml: 2 }}>
                <Headline
                  text={"Metrics"}
                  height={"5vh"}
                  // backgroundColor={'rgb(60,232,170)'}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ padding: 2 }}>
              <Grid item xs={viewType === "doctor" ? 12 : 12}>
                <ResultsMetrics />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ padding: 2 }}>
              <Grid item xs={12}>
                <FlexionAnalysisContainer 
                  timeSeriesData={gaitResults.time_series_data}
                  flexionSummary={gaitResults.flexion_summary}
                  statistics={gaitResults.statistics}
                />
              </Grid>
              <Grid item xs={12}>
                {/*<SymmetryInfo />*/}
              </Grid>
            </Grid>
          </div>
        )}
      </Paper>
      <ToastContainer />
    </div>
  );
}